"use client";

import ArticleAbstract from "@/src/components/journal-site/articles/article-abstract/article-abstract";
import ArticleAcknowledgments from "@/src/components/journal-site/articles/article-acknowledgments/article-acknowledgments";
import ArticleAuthors from "@/src/components/journal-site/articles/article-authors/article-authors";
import ArticleBody from "@/src/components/journal-site/articles/article-body/article-body";
import ArticleCopyRight from "@/src/components/journal-site/articles/article-copyright/article-copyright";
import ArticleCopyrightStatement from "@/src/components/journal-site/articles/article-sidebar/article-copyright-statement/article-copyright-statement";
import ArticleCrossmark from "@/src/components/journal-site/articles/article-sidebar/article-crossmark/article-crossmark";
import ArticleDoi from "@/src/components/journal-site/articles/article-doi/article-doi";
import ArticleFootnotes from "@/src/components/journal-site/articles/article-footnotes/article-footnotes";
import ArticleHighlights, {
  hasHighlightsContent,
} from "@/src/components/journal-site/articles/article-highlights/article-highlights";
import ArticleIssueNavigationBar from "@/src/components/journal-site/articles/article-issue-navigation/article-issue-navigation";
import ArticleKeywords from "@/src/components/common/keywords/keywords";
import ArticleMetaDataComponent from "@/src/components/journal-site/articles/article-meta-data/article-meta-data";
import ArticleOutlines from "@/src/components/journal-site/articles/article-outlines/article-outlines";
import ArticleReferences from "@/src/components/journal-site/articles/article-references/article-references";
import ArticleRelatedArticles from "@/src/components/journal-site/articles/article-related-articles";
import ArticleSidebar from "@/src/components/journal-site/articles/article-sidebar/article-sidebar";
import ArticleSimilarArticles from "@/src/components/journal-site/articles/article-similar-articles/article-similar-articles";
import AuthorAffiliation from "@/src/components/journal-site/articles/author-affiliation/author-affiliation";
import AuthorCorrespondence from "@/src/components/journal-site/articles/author-correspondence/author-correspondence";
import CitationData from "@/src/_types/Article/article-citation-data";
import dynamic from "next/dynamic";
import HowToCite from "@/src/components/journal-site/articles/how-to-cite/how-to-cite";
import CoverPlate from "@/src/components/common/broadsheet/cover-plate";
import {
  imageCredit,
  splitCreditLinks,
  withAiDisclosure,
} from "@/src/utils/image-credit";
import { stripHTMLTags } from "@/src/utils/strip-html-tags";
import VISUALLY_HIDDEN from "@/src/utils/visually-hidden";
import PageContent from "@/src/components/journal-site/common/page-content/page-content";
import ReactHtmlParser from "@/src/utils/html-parser";
import SpanTypography from "@/src/mui-custom/typography/span-typography/span-typography";
import { Box, Divider, Typography } from "@mui/material";
import {
  FONT_DISPLAY,
  FONT_UI,
  HEADING_SIZE,
  RAIL_LEFT,
  RAIL_RIGHT,
  SHEET_GUTTER,
  spineForJournal,
  STICKY_TOP,
  stickyRailSx,
} from "@/src/components/common/broadsheet/tokens";
import type ArticleBodyProps from "@/src/_types/Article/article-body";
import type ArticleIssueNavigation from "@/src/_types/Article/article-issue-navigation";

const CommentComponent = dynamic(
  () => import("@/src/components/common/comment"),
  {
    ssr: false,
  },
);

export default function ArticleComponent({
  journal,
  document,
  citation,
  article,
  isDraftEnabled,
  similarArticles = [],
  issueNavigation,
  publisher,
}: {
  journal: Journal;
  document: Article;
  citation: CitationData[];
  article: string;
  isDraftEnabled: boolean;
  similarArticles?: ArticleCardData[];
  issueNavigation?: ArticleIssueNavigation;
  publisher?: string;
}) {
  return (
    <Box
      sx={{
        px: SHEET_GUTTER,
        pt: { xs: 1, md: 1.25 },
        pb: { xs: 1.5, md: 2.5 },
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          lg: `${RAIL_LEFT} 1fr ${RAIL_RIGHT}`,
        },
        columnGap: 2.5,
        rowGap: 1.5,
        alignItems: "start",
        "& > *": { minWidth: 0 },
      }}
    >
        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            ...stickyRailSx,
            pt: 1.25,
            pr: 1.25,
            borderRight: "1px solid",
            borderColor: "custom.ruleHairline",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {document.feature_image_thumbnail?.content ? (
              <Box
                sx={{
                  width: "100%",

                  mb: {
                    xs: 0,
                    sm: 0,
                    md: 1.5,
                  },
                }}
              >
                <CoverPlate
                  src={document.feature_image_thumbnail.content}
                  alt={withAiDisclosure(
                    `Cover image of the article: ${stripHTMLTags(document.title || "")}`,
                    document.feature_image_thumbnail,
                  )}
                  width={176}
                  height={176}
                  offset={5}
                  framed
                  spine={spineForJournal(journal)}
                />
                <Typography
                  component="p"
                  sx={{
                    pt: 0.25,
                    wordBreak: "break-word",
                    fontFamily: FONT_UI,
                    fontSize: "0.6875rem",
                    lineHeight: 1.45,
                    color: "custom.inkMuted",
                  }}
                >
                  <SpanTypography
                    variant="inherit"
                    sx={{
                      mr: 0.25,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    Image Credit:
                  </SpanTypography>
                  <SpanTypography variant="inherit">
                    {splitCreditLinks(
                      imageCredit(document.feature_image_thumbnail, journal),
                    ).map((part, index) =>
                      part.kind === "text" ? (
                        part.value
                      ) : (
                        <Box
                          key={index}
                          component="a"
                          href={part.href}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          sx={{
                            paddingBlock: "4px",
                            color: "inherit",
                            textDecoration: "underline",
                          }}
                        >
                          Link
                          <Box component="span" sx={VISUALLY_HIDDEN}>
                            {" "}
                            to image source (opens in a new tab)
                          </Box>
                        </Box>
                      ),
                    )}
                  </SpanTypography>
                </Typography>
              </Box>
            ) : (
              <Typography
                component="div"
                sx={{
                  fontSize: 175,
                  lineHeight: 0,
                  mb: { xs: 0, sm: 0, md: 1.5 },
                }}
              >
                </Typography>
            )}
            <Box
              sx={{
                width: "100%",
              }}
            >
              <ArticleOutlines
                hasAbstract={
                  document?.abstract && document?.abstract.length > 0
                }
                hasAcknowledgments={
                  document?.acknowledgments &&
                  document?.acknowledgments.length > 0
                }
                hasFootnotes={
                  document?.footnotes && document?.footnotes.length > 0
                }
                hasReferences={
                  document?.references && document?.references.length > 0
                }
                hasCopyright={document?.copyright !== ""}
              />
            </Box>
          </Box>
        </Box>
        <Box>
          <Box>
            <Box>
              {document?.doi && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    columnGap: 1,
                    rowGap: 0.25,
                    mb: 0.5,
                  }}
                >
                  <ArticleDoi articleDoi={document.doi} />
                  <ArticleCrossmark doi={document.doi} />
                </Box>
              )}
              <Typography
                variant="h1"
                sx={{
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 700,
                  fontSize: HEADING_SIZE.page,
                  lineHeight: 1.1,
                  letterSpacing: "0.01em",
                  color: "custom.ink",
                  mb: 0.625,
                }}
              >
                {ReactHtmlParser(document?.title || "")}
              </Typography>
              <ArticleAuthors authors={document?.authors || []} />
              {document?.affiliations && document?.affiliations.length > 0 && (
                <Box
                  id="authors-affiliations"
                  sx={{
                    mb: 0.5,
                  }}
                >
                  <AuthorAffiliation
                    affiliations={document?.affiliations || []}
                  />
                </Box>
              )}
              {document?.correspondences &&
                document?.correspondences.length > 0 && (
                  <Box id="article-correspondences">
                    <AuthorCorrespondence
                      correspondences={document?.correspondences || []}
                      hasEqualContributer={
                        document?.authors.some(
                          (author: Author) =>
                            author.equal_contrib &&
                            author.equal_contrib === "yes",
                        ) || false
                      }
                      isDeceased={
                        document?.authors.some(
                          (author: Author) =>
                            author?.deceased && author.deceased !== "",
                        ) || false
                      }
                      authorNoteFnEqual={
                        document ? document.author_note_fn_equal : ""
                      }
                    />
                  </Box>
                )}
              <Divider
                sx={{
                  marginTop: "1rem",
                  marginBottom: "1rem",
                  borderColor: "custom.ruleHairline",
                  opacity: 0.25,
                }}
              />
              <ArticleMetaDataComponent
                journalTitle={journal.title || ""}
                articleMetaData={document}
                issueUrl={`/journals/${journal.path}/issue/${document.issue_id}`}
              />
              {(document.related_articles.length > 0 ||
                document.source_related_articles.length > 0) && (
                <ArticleRelatedArticles
                  relatedArticles={document.related_articles || []}
                  sourceRelatedArticles={document.source_related_articles || []}
                />
              )}
              <HowToCite howTociteDictionary="How to Cite:" data={document} />
            </Box>
            {document?.abstract && document?.abstract.length > 0 && (
              <PageContent
                sx={{
                  mb: 0.5,
                }}
              >
                <Box id="article-abstract">
                  <ArticleAbstract abstracts={document?.abstract} />
                </Box>
              </PageContent>
            )}

            {hasHighlightsContent(document?.highlights) && (
              <PageContent
                sx={{
                  mb: 0.5,
                }}
              >
                <Box id="article-highlights">
                  <ArticleHighlights
                    highlights={document?.highlights as string}
                  />
                </Box>
              </PageContent>
            )}

            {document?.keywords && document?.keywords?.length > 0 && (
              <Box sx={{ mb: 1.25 }}>
                <ArticleKeywords
                  journalPath={journal.path}
                  isMainSite={journal.is_main_site}
                  keywords={document?.keywords}
                />
              </Box>
            )}
            {!isDraftEnabled && issueNavigation && (
              <Box sx={{ mb: 0.5 }}>
                <ArticleIssueNavigationBar
                  navigation={issueNavigation}
                  journalAbbr={journal.meta_data?.title_abbr || ""}
                />
              </Box>
            )}
            {((document?.body && document?.body.sections.length > 0) ||
              (document?.footnotes && document.footnotes.length > 0) ||
              (document?.acknowledgments &&
                document.acknowledgments.length > 0)) && (
              <PageContent
                sx={{
                  mb: 0.5,
                }}
              >
                {document?.body && document.body.sections.length > 0 && (
                  <Box id="article-body">
                    <ArticleBody
                      articleBody={document?.body?.sections}
                      journalAbbr={journal.meta_data?.title_abbr || ""}
                      documentId={Number(article)}
                      isDraftEnabled={isDraftEnabled}
                      figures={isDraftEnabled ? document?.figures : undefined}
                    />
                  </Box>
                )}
                {document?.acknowledgments &&
                  document.acknowledgments.length > 0 && (
                    <Box id="article-acknowledgments">
                      <ArticleAcknowledgments
                        acknowledgments={document?.acknowledgments}
                      />
                    </Box>
                  )}
                {document?.footnotes && document.footnotes.length > 0 && (
                  <Box id="article-footnotes">
                    <ArticleFootnotes footnotes={document?.footnotes} />
                  </Box>
                )}
              </PageContent>
            )}
            {document?.references && document.references.length > 0 && (
              <PageContent
                sx={{
                  mb: 0.5,
                }}
              >
                <Box id="article-references">
                  <ArticleReferences references={document?.references} />
                </Box>
              </PageContent>
            )}
            {document?.copyright && document.copyright !== "" && (
              <PageContent>
                <Box id="article-copyright">
                  <ArticleCopyRight copyright={document?.copyright} />
                  <ArticleCopyrightStatement
                    statement={
                      journal.options?.template_configs?.article_sidebar
                        ?.statement
                    }
                    doi={document?.doi}
                  />
                </Box>
              </PageContent>
            )}
            {similarArticles.length > 0 && (
              <PageContent
                sx={{
                  mt: 0.5,
                }}
              >
                <Box id="article-similar-articles">
                  <ArticleSimilarArticles
                    similarArticles={similarArticles}
                    authors={document?.authors || []}
                    articleTitle={document?.title || ""}
                  />
                </Box>
              </PageContent>
            )}
            <CommentComponent
              journalSlug={journal.slug}
              entityId={document.id || ""}
              entity="document"
            />
          </Box>
        </Box>
        <Box
          sx={{
            position: { lg: "sticky" },
            top: { lg: STICKY_TOP },
            alignSelf: "start",
            pt: { lg: 1.25 },
            pl: { lg: 1.25 },
            borderLeft: { lg: "1px solid" },
            borderColor: { lg: "custom.ruleHairline" },
          }}
        >
          <ArticleSidebar
            data={document as Article & ArticleBodyProps & ArticleFiles}
            journal={journal}
            publisher={publisher}
            journalAbbr={journal.meta_data?.title_abbr}
            articleId={Number(article)}
            citation={citation}
            statement={
              journal.options?.template_configs?.article_sidebar?.statement
            }
            eISSN={journal.meta_data?.issn_epub || ""}
            hasPlumx={journal.services?.plumix_metrics?.status}
          />
        </Box>
    </Box>
  );
}
